import React, { useEffect, useState } from 'react';

import cx from 'classnames';
import Portal from 'components/Portal';
import { IKImage } from 'imagekitio-react';
import Draggable from 'react-draggable';
import Moveable from "react-moveable";

import { Property } from '../../components/properties/';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCross, faTimes } from '@fortawesome/free-solid-svg-icons';

import styles from './image.module.scss';
import { Button } from 'react-bootstrap';
import _ from 'lodash';

const maskTypes =  [
  'square', 'circle', 'triangle', 'hexagon', 'octagon', 'star', 'egg', 'diamond', 'semi_circle', 'heart', 'moon', 'plus', 'trapezium', 'extended_arrow', 'arrow'
]

const maskImgBaseURL = process.env.REACT_APP_CLOUD_FRONT_URL + '/public/cleverstory/v2/mask/';

export const ImageCropper = ({ editorSession, id, image, width, height, show, crop, onCrop, onCancel }) => {
  const cropperRef = React.useRef(null);
  const imageRef = React.useRef(null);

  const [settings, setSettings] = useState({
    dialogPosition: null,
    image: image,
    mask: '',
    element: {
      x: 0,
      y: 0,
      width: parseInt(width),
      height: parseInt(height)
    },
    inner: {
      x: _.get(crop, 'x', 0),
      y: _.get(crop, 'y', 0),
      originX: 50,
      originY: 50,
      width: parseInt(width),
      height: parseInt(height)
    },
    outer: {
      x: 0,
      y: 0,
      width: _.get(crop, 'zoomWidth', parseInt(width)),
      height: _.get(crop, 'zoomHeight', parseInt(height))
    }
  })

  const handleMaskChange = (mask) => {
    setSettings((state)=>{
      return{
        ...state,
        mask:`${maskImgBaseURL}${mask}.svg`
      }
    })
  }

  useEffect(() => {
    if (!show) {
      return;
    }

    calculatePosition();
    document.querySelector(`#${editorSession}`).classList.add(styles.disableScroll);

    return () => {
      document.querySelector(`#${editorSession}`).classList.remove(styles.disableScroll);
    }
  }, []);

  useEffect(() => {
    if (!show) {
      return;
    }

    calculateSettings();
  }, [show]);

  const calculateSettings = () => {
    const innerWidth = _.get(crop, 'width', parseInt(width));
    const innerHeight = _.get(crop, 'height', parseInt(height));

    const scaleX = parseInt(width) / innerWidth;
    const scaleY = parseInt(height) / innerHeight;

    const zoomWidth = scaleX * _.get(crop, 'zoomWidth', parseInt(width));
    const zoomHeight = scaleY * _.get(crop, 'zoomHeight', parseInt(height));

    const mask = _.get(crop, 'mask', `${maskImgBaseURL}${maskTypes[0]}.svg`);

    const settings = {
      dialogPosition: null,
      image: image,
      mask: mask,
      element: {
        x: 0,
        y: 0,
        width: parseInt(width),
        height: parseInt(height)
      },
      inner: {
        x: _.get(crop, 'x', 0) * scaleX,
        y: _.get(crop, 'y', 0) * scaleY,
        originX: 50,
        originY: 50,
        width: parseInt(width),
        height: parseInt(height)
      },
      outer: {
        x: 0,
        y: 0,
        width: zoomWidth,
        height: zoomHeight
      }
    }

    setSettings(settings);
    calculatePosition();
  }

  const calculatePosition = () => {
    const element = document.querySelector(`#${id}`);
    const rect = element.getBoundingClientRect();

    const imagePosition = {
      x: rect.left,
      y: rect.top
    }

    setSettings((state) => {
      return {
        ...state,
        element: {
          ...state.element,
          ...imagePosition
        },
        outer: {
          ...state.outer,
          x: rect.left - state.inner.x,
          y: rect.top - state.inner.y
        }
      }
    });
  }

  const handleSettingsChange = (data) => {

    setSettings((state) => {
      const originX = state.inner.originX;
      const originY = state.inner.originY;

      const deltaW = data - state.outer.width;
      var outerX = state.outer.x - (deltaW * originX / 100);
      outerX = Math.min(outerX, state.element.x);
      outerX = Math.max(outerX, state.element.x + state.element.width - state.outer.width);

      const height = data * state.outer.height / state.outer.width;
      const deltaH = height - state.outer.height;
      var outerY = state.outer.y - (deltaH * originY / 100);
      outerY = Math.min(outerY, state.element.y);
      outerY = Math.max(outerY, state.element.y + state.element.height - state.outer.height);

      var innerX = state.inner.x + (deltaW * originX / 100);
      innerX = Math.max(0, innerX);
      innerX = Math.min(innerX, state.outer.width - state.inner.width);

      var innerY = state.inner.y + (deltaH * originY / 100);
      innerY = Math.max(0, innerY);
      innerY = Math.min(innerY, state.outer.height - state.inner.height);

      return {
        ...state,
        inner: {
          ...state.inner,
          x: innerX,
          y: innerY
        },
        outer: {
          ...state.outer,
          x: outerX,
          y: outerY,
          width: data,
          height: height
        }
      }
    })
  }

  const innerStyle = {
    left: settings.inner.x,
    top: settings.inner.y,
    width: settings.inner.width,
    height: settings.inner.height,
    maskSize: '100% 100%',
    WebkitMaskSize: '100% 100%',
    maskImage: `url(${settings.mask})`,
    WebkitMaskImage: `url(${settings.mask})`
  }

  const handleDrag = (e, data) => {
    setSettings((state) => {

      const deltaX = data.x - state.outer.x;
      const deltaY = data.y - state.outer.y;

      const originX = state.inner.originX - (deltaX * 50 / state.outer.width);
      const originY = state.inner.originY - (deltaY * 50 / state.outer.height);

      return {
        ...state,
        outer: {
          ...state.outer,
          x: data.x,
          y: data.y
        },
        inner: {
          ...state.inner,
          originX: originX,
          originY: originY,
          x: state.inner.x - deltaX,
          y: state.inner.y - deltaY
        }
      }
    })
  }

  const handleResize = (data) => {
    setSettings((state) => {
      var { x, y, width, height } = data;

      x = Math.max(x, 0);
      y = Math.max(y, 0);
      width = Math.min(width, state.outer.width);
      height = Math.min(height, state.outer.height);

      return {
        ...state,
        inner: {
          ...state.inner,
          ...data,
          x: x,
          y: y,
          width: width,
          height: height
        },
        element: {
          ...state.element,
          x: state.outer.x + x,
          y: state.outer.y + y,
          width: width,
          height: height
        }
      }
    })
  }

  const handleFinish = () => {
    const crop = {
      x: settings.inner.x,
      y: settings.inner.y,
      width: settings.inner.width,
      height: settings.inner.height,
      zoomWidth: settings.outer.width,
      zoomHeight: settings.outer.height,
      mask: settings.mask
    }

    const element = document.querySelector(`#${id}`);
    const rect = element.getBoundingClientRect();
    
    const layout = {
      ...settings.element,
      deltaX: settings.element.x - rect.left,
      deltaY: settings.element.y - rect.top
    }

    onCrop(crop, layout);
  }

  return (
    <Portal>
      <div ref={cropperRef} className={cx(styles.cropper, {
        [styles.show]: show
      })}>
        <Draggable onDrag={handleDrag} position={{
          x: settings.outer.x,
          y: settings.outer.y
        }}
          bounds={{
            left: settings.element.x + settings.element.width - settings.outer.width,
            right: settings.element.x,
            top: settings.element.y + settings.element.height - settings.outer.height,
            bottom: settings.element.y
          }}
          cancel={'.moveable-control'}
        >
          <div className={cx(styles.outer, styles.handle)}>
            <IKImage
              ref={imageRef}
              path={image ? image.url : ''}
              width={settings.outer.width}
              height={settings.outer.height}
              draggable="false"
              transformation={[{
                format: 'auto',
                effectUSM: '0.66-1.00-0.01'
              }]}
            />

            <div className={cx(styles.inner, styles.handle)} style={innerStyle}>
              <IKImage
                ref={imageRef}
                path={image ? image.url : ''}
                width={settings.outer.width}
                height={settings.outer.height}
                draggable="false"
                transformation={[{
                  format: 'auto',
                  effectUSM: '0.66-1.00-0.01'
                }]}
                style={{
                  top: -settings.inner.y,
                  left: -settings.inner.x
                }}
              />
            </div>
            <Resizable
              target={`.${styles.inner}`}
              dimension={settings.inner}
              onResize={handleResize}
            />
          </div>
        </Draggable>

        {show &&
          <CropperSettings
            id={id}
            image={image}
            value={settings}
            onChange={handleSettingsChange}
            onCrop={handleFinish}
            onCancel={onCancel}
            handleMaskChange={handleMaskChange}
          />
        }
      </div>
    </Portal >
  );
}

const CropperSettings = ({ id, value, image, onChange, onCrop, onCancel, handleMaskChange }) => {

  if(value && value.image){
    var { width, height } = value.inner;
    var aspectRatio = value.image.width / value.image.height;

    width = Math.max(width, height * aspectRatio);
  }

  return (
    <Property.Dialog
      className={styles.cropperDialog}
      width={260}
      target={`#${id}`}
    >
      <Property.Header title={'Crop Image'}>
        <Button className={cx(styles.action,styles.confirm)} onClick={onCrop}>
          <i className={cx("cleverstory-tick")} />
        </Button>
        <Button className={styles.action}  onClick={onCancel}>
          <i className='cleverstory-close' />
        </Button>
      </Property.Header>
      <Property.Body>
          <Property  className={styles.zoomImage}>
            <h5 className={styles.title}>Zoom image</h5>
            <Property.Slider
              min={width}
              max={width * 4}
              step={1}
              value={value.outer.width}
              onChange={(data) => {
                onChange(data);
              }}
            />
          </Property>

          <Property className={styles.dialogBody}>
          <div className={styles.maskContainer}>
            <h5 className={styles.title}>Crop with shape</h5>
            <div className={styles.grid}>
              {
                maskTypes.map((mask, index) => {
                  const active = value.mask === `${maskImgBaseURL}${mask}.svg`;

                  const maskProperties = {
                    maskImage: `url(${maskImgBaseURL}${mask}.svg)`,
                    WebkitMaskImage: `url(${maskImgBaseURL}${mask}.svg)`,
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    WebkitMaskposition: 'center'
                  }

                  return (
                    <div key={index} 
                      active={active}
                      className={cx(styles.maskTypeButton, {
                        [styles.active]: active
                      })}
                      onClick={() => handleMaskChange(mask)}
                    >
                      <div className={styles.icon}>
                        <div style={maskProperties}>
                            <IKImage
                              path={image ? image.url : ''}
                              width={60}
                              height={60}
                              className={styles.mask}
                              draggable="false"
                              transformation={[{
                                format: 'auto',
                                effectUSM: '0.66-1.00-0.01'
                              }]}
                            />
                        </div>
                        
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
          </Property>
      </Property.Body>
    </Property.Dialog>

  );
}

const Resizable = ({ target, dimension, onResize, onResizeEnd }) => {
  const moveableRef = React.useRef();
  const [targetElement, setTargetElement] = React.useState();
  const [frame, setFrame] = React.useState({
    translate: [dimension.x, dimension.y]
  });

  React.useEffect(() => {
    setTargetElement(document.querySelector(target));
  }, []);

  useEffect(() => {
    setFrame({
      translate: [dimension.x, dimension.y]
    })
    moveableRef.current.updateRect();
  }, [dimension]);

  return (
    <Moveable
      ref={moveableRef}
      target={targetElement}
      resizable={true}
      keepRatio={false}
      renderDirections={['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se']}
      edge={false}
      origin={false}
      zoom={1}
      onResizeStart={e => {
        e.setOrigin(["%", "%"]);
        e.dragStart && e.dragStart.set(frame.translate);
      }}
      onResize={e => {
        const beforeTranslate = e.drag.beforeTranslate;
        frame.translate = beforeTranslate;

        const change = {
          ...dimension,
          width: e.width,
          height: e.height,
          x: beforeTranslate[0],
          y: beforeTranslate[1]
        }

        onResize(change);
      }}
    />
  );
}